import React, { useContext, useEffect, useMemo } from "react";
import {
  Button,
  Grid,
  IconButton,
  Icons,
  Text,
  Loader,
} from "@pushpress/shared-components";
import { Helmet } from "react-helmet";
import { useHistory } from "react-router-dom";
import { PRIMARY_COLOR } from "../../../constants/colors";
import {
  filterAttendees,
  filterLateCancel,
  filterWaitlist,
} from "../Calendar.utils";
import AttendanceTable from "./tables/AttendanceTable";
import WaitlistTable from "./tables/WaitlistTable";
import LateCancelTable from "./tables/LateCancelTable";
import { defaultToastOptions, extractError, sleep } from "../../../utils/misc";
import useToggle from "../../../hooks/state";
import { toast } from "react-toastify";
import AddMemberModal from "../AddMemberModal";
import AddMemberToEventModal from "../AddMemberToEventModal";
import {
  useGetCalendarItemDetailQuery,
  useMassSmsCalendarItemMutation,
  useMassEmailCalendarItemMutation,
} from "./CalendarDetail-generated.hooks";
import { AppContext } from "../../../context/app";
import useDownload from "../../../hooks/useDownload";
import SendEmailModal from "../../../components/modals/SendEmailModal";
import { SendEmailForm } from "../../../components/modals/SendEmailModal/SendEmailModal";
import SendTextModal from "../../../components/modals/SendTextModal";
import { SendTextForm } from "../../../components/modals/SendTextModal/SendTextModal";
import {
  createClassUsersCsv,
  generateClassUsersFileName,
} from "./CalendarDetail.utils";
import ClassSummary from "./ClassSummary";
import styles from "./CalendarDetail.module.scss";

type CalendarDetailProps = { uuid: string };

export const CalendarDetail = ({ uuid }: CalendarDetailProps) => {
  const history = useHistory();
  const { client } = useContext(AppContext);
  const clientUuid = client.uuid || client.client_uuid;

  const { data, loading, refetch } = useGetCalendarItemDetailQuery({
    variables: {
      getCalendarItemInput: {
        uuid,
        clientUuid,
      },
    },
    notifyOnNetworkStatusChange: true,
  });
  const [sendMassEmail, { loading: massEmailLoading }] =
    useMassEmailCalendarItemMutation({
      onError(err) {
        toast.error(extractError(err), defaultToastOptions);
      },
    });
  const [sendMassText, { loading: massSmsLoading }] =
    useMassSmsCalendarItemMutation({
      onError(err) {
        toast.error(extractError(err), defaultToastOptions);
      },
    });
  const [showAddMemberModal, showAddMemberModalActions] = useToggle(false);
  const [showAddMemberToEventModal, showAddMemberToEventModalActions] =
    useToggle(false);
  const [sendEmailVisible, sendEmailActions] = useToggle(false);
  const [sendTextVisible, sendTextActions] = useToggle(false);
  const calendarItem = data?.getCalendarItem;

  const { handleDownload: handleDownloadUsers } = useDownload({
    fileName: generateClassUsersFileName(
      client.timezone,
      data?.getCalendarItem?.title,
      data?.getCalendarItem?.startDatetime,
    ),
    async getFileContent() {
      const attendees = data?.getCalendarItem?.attendees;
      return createClassUsersCsv(client.timezone, attendees);
    },
    onError(err) {
      toast.error(extractError(err));
    },
  });

  useEffect(() => {
    // If class doesn't exist, we redirect to the main view
    if (!data?.getCalendarItem && !loading) {
      toast.warning(`Class/Event not found`, defaultToastOptions);
      history.push("/schedule");
    }
  }, [data?.getCalendarItem, loading, history]);

  const coachIds = useMemo(
    () =>
      [
        calendarItem?.mainCoach?.userUuid,
        calendarItem?.assistantCoach?.userUuid,
      ].filter((userUuid) => userUuid),
    [calendarItem?.mainCoach?.userUuid, calendarItem?.assistantCoach?.userUuid],
  );

  const registrations = useMemo(
    () => ({
      attendees: filterAttendees(calendarItem?.attendees || [], coachIds),
      waitlist: filterWaitlist(calendarItem?.attendees || []),
      lateCancel: filterLateCancel(calendarItem?.attendees || []),
    }),
    [calendarItem?.attendees, coachIds],
  );

  const handleRefetchData = async () => {
    // Doing this cause the update is not instantaneous on the backend
    await sleep(1000);
    refetch();
  };

  const handleSendEmail = async ({
    bccAdmins,
    bccStaff,
    bccYourself,
    message,
    subject,
  }: SendEmailForm) => {
    const { data } = await sendMassEmail({
      variables: {
        input: {
          calendarUuid: uuid,
          subject,
          message,
          bcc: bccYourself,
          bccAdmins,
          bccStaff,
        },
      },
    });
    if (data) {
      toast.success("Email sent", defaultToastOptions);
      sendEmailActions.off();
    }
  };

  const handleSendText = async ({ message }: SendTextForm) => {
    const { data } = await sendMassText({
      variables: {
        input: {
          calendarUuid: uuid,
          message,
        },
      },
    });
    if (data) {
      toast.success("Text message sent", defaultToastOptions);
      sendTextActions.off();
    }
  };

  if (!calendarItem)
    return (
      <Grid container justifyContent="center" alignItems="center" marginY={5}>
        <Grid item>
          <Loader />
        </Grid>
      </Grid>
    );

  const totalCheckInAttendees = registrations?.attendees.filter((item) =>
    ["checkedin", "reserved"].includes(item.status),
  ).length;
  const sendMessage = `Sending to (${totalCheckInAttendees}) attendees`;
  const shouldShowSms = ["US", "CA"].includes(client.countryIso2);
  const shouldShowEmail = !!client.isMassEmailEnabled;

  return (
    <>
      <Helmet>
        <title>{calendarItem.title}</title>
      </Helmet>
      <AddMemberModal
        visible={showAddMemberModal}
        onClose={showAddMemberModalActions.off}
        onSuccess={() => {
          showAddMemberModalActions.off();
          refetch();
        }}
        calendarUuid={uuid}
      />
      <AddMemberModal
        visible={showAddMemberModal}
        onClose={showAddMemberModalActions.off}
        onSuccess={() => {
          showAddMemberModalActions.off();
          refetch();
        }}
        calendarUuid={uuid}
      />
      <AddMemberToEventModal
        visible={showAddMemberToEventModal}
        onClose={showAddMemberToEventModalActions.off}
        onSuccess={() => {
          showAddMemberToEventModalActions.off();
          refetch();
        }}
        calendarUuid={uuid}
      />
      <SendEmailModal
        visible={sendEmailVisible}
        title="Email attendees"
        loading={massEmailLoading}
        message={sendMessage}
        onClose={sendEmailActions.off}
        onSendClick={handleSendEmail}
      />
      <SendTextModal
        visible={sendTextVisible}
        title="Text attendees"
        loading={massSmsLoading}
        message={sendMessage}
        onClose={sendTextActions.off}
        onSendClick={handleSendText}
      />
      <Grid container spacing={3}>
        <Grid item xs={12} md={4} xl={3}>
          <Grid
            container
            alignItems="center"
            className={`${styles.pageHeader} mb-4`}
          >
            <Grid item>
              <IconButton
                color={PRIMARY_COLOR}
                icon={Icons.ArrowLeft}
                onClick={() =>
                  history.length > 0
                    ? history.goBack()
                    : history.push("/schedule")
                }
              />
            </Grid>
            <Grid item>
              <Text variant="heading-4">{calendarItem.type} info</Text>
            </Grid>
          </Grid>
          <ClassSummary calendarItem={calendarItem} refetch={refetch} />
        </Grid>
        <Grid item xs={12} md={8} xl={9}>
          <Grid
            container
            className={`${styles.pageHeader} mb-4`}
            alignItems="center"
            justifyContent="space-between"
          >
            <Grid item>
              <Text variant="heading-4">Attendance</Text>
            </Grid>
            <Grid item>
              <Grid container alignItems="center" spacing={4}>
                <Grid item>
                  <IconButton
                    icon={Icons.Download}
                    tooltip={`Export ${
                      calendarItem?.type ?? "class"
                    } registrations`}
                    onClick={handleDownloadUsers}
                  />
                </Grid>
                {shouldShowSms && (
                  <Grid item>
                    <IconButton
                      icon={Icons.TextMessage}
                      tooltip="Send text to all attendees"
                      onClick={sendTextActions.on}
                    />
                  </Grid>
                )}
                {shouldShowEmail && (
                  <Grid item>
                    <IconButton
                      icon={Icons.Email}
                      tooltip="Send email to all attendees"
                      onClick={sendEmailActions.on}
                    />
                  </Grid>
                )}
                <Grid item>
                  <Button
                    buttonType="primary"
                    size="large"
                    icon={Icons.Plus}
                    iconLocation="left"
                    text="Add"
                    onClick={
                      calendarItem.type === "Class"
                        ? showAddMemberModalActions.on
                        : showAddMemberToEventModalActions.on
                    }
                  />
                </Grid>
              </Grid>
            </Grid>
          </Grid>
          <AttendanceTable
            attendees={registrations.attendees}
            onActionSuccess={handleRefetchData}
          />
          {registrations.waitlist.length > 0 && (
            <>
              <Text variant="heading-4" className="mt-5 mb-3">
                Waitlist
              </Text>
              <WaitlistTable
                attendees={registrations.waitlist}
                onActionSuccess={handleRefetchData}
              />
            </>
          )}
          {registrations.lateCancel.length > 0 && (
            <>
              <Text variant="heading-4" className="mt-5 mb-3">
                Late cancel
              </Text>
              <LateCancelTable
                attendees={registrations.lateCancel}
                onActionSuccess={handleRefetchData}
              />
            </>
          )}
        </Grid>
      </Grid>
    </>
  );
};
